import { ParamMap } from '@angular/router';

import {
  GeoBounds,
  parseGeoBounds,
  serializeGeoBounds,
} from './featured-properties-bounds.util';
import {
  applyTypeFilterValue,
  DEFAULT_LOT_SIZE_MAX,
  DEFAULT_PRICE_PER_SQFT_MAX,
  DEFAULT_SQFT_MAX,
  DEFAULT_YEAR_BUILT_MAX,
  getCurrentTypeFilter,
  ListingTypeFilter,
  normalizeListingStatusFilterValue,
  parseFilterNumber,
  parseYesNoFilter,
  TypesToDisplayState,
  YesNoFilter,
} from './featured-properties-filters.util';
import { ListingStatusFilterKey } from '@app/utils/listing-status.util';

export interface FilterUrlState {
  searchText: string;
  searchBounds: GeoBounds | null;
  mapBounds: GeoBounds | null;
  filterByMapBounds: boolean;
  typesToDisplay: TypesToDisplayState;
  minPrice: number;
  maxPrice: number;
  bedsMin: number;
  minBaths: number;
  mlsPropertyTypes: string[];
  schoolDistricts: string[];
  stories: string[];
  listingStatuses: string[];
  lotSizeMin: number;
  lotSizeMax: number;
  sqftMin: number;
  sqftMax: number;
  pricePerSqFtMin: number;
  pricePerSqFtMax: number;
  maxDaysOnMarket: number;
  yearBuiltMin: number;
  yearBuiltMax: number;
  requireVirtualTour: boolean;
  poolFilter: YesNoFilter;
  fireplaceFilter: YesNoFilter;
  requireGarage: boolean;
  includeRentals: boolean;
  areaMin: number;
  areaMax: number;
  sizeMin: number;
  sizeMax: number;
  sort: 'ascending' | 'descending' | null;
}

export const DEFAULT_FILTER_URL_STATE: FilterUrlState = {
  searchText: '',
  searchBounds: null,
  mapBounds: null,
  filterByMapBounds: false,
  typesToDisplay: {
    residentialProperties: true,
    commercialProperties: true,
    industrialProperties: true,
    landProperties: true,
  },
  minPrice: 0,
  maxPrice: 5_000_000,
  bedsMin: 0,
  minBaths: 0,
  mlsPropertyTypes: [],
  schoolDistricts: [],
  stories: [],
  listingStatuses: [],
  lotSizeMin: 0,
  lotSizeMax: DEFAULT_LOT_SIZE_MAX,
  sqftMin: 0,
  sqftMax: DEFAULT_SQFT_MAX,
  pricePerSqFtMin: 0,
  pricePerSqFtMax: DEFAULT_PRICE_PER_SQFT_MAX,
  maxDaysOnMarket: 0,
  yearBuiltMin: 0,
  yearBuiltMax: DEFAULT_YEAR_BUILT_MAX,
  requireVirtualTour: false,
  poolFilter: 'any',
  fireplaceFilter: 'any',
  requireGarage: false,
  includeRentals: false,
  areaMin: 0,
  areaMax: 1000,
  sizeMin: 0,
  sizeMax: 100_000,
  sort: 'descending',
};

export function parseFilterQueryParams(params: ParamMap): Partial<FilterUrlState> {
  const patch: Partial<FilterUrlState> = {};

  const address = params.get('address')?.trim();
  if (address) {
    patch.searchText = address;
  }

  const bounds = parseGeoBounds(params.get('bounds'));
  if (bounds) {
    patch.searchBounds = bounds;
  }

  const mapBounds = parseGeoBounds(params.get('mapBounds'));
  if (mapBounds) {
    patch.mapBounds = mapBounds;
  }

  const mapFilter = params.get('mapFilter');
  if (mapFilter === '1' || mapFilter === 'true') {
    patch.filterByMapBounds = true;
  } else if (mapFilter === '0' || mapFilter === 'false') {
    patch.filterByMapBounds = false;
  }

  const status = params.get('status')?.trim();
  if (status) {
    patch.listingStatuses = status
      .split(',')
      .map((value) => normalizeListingStatusFilterValue(value.trim()))
      .filter((value): value is ListingStatusFilterKey => value !== null);
  }

  const type = params.get('type')?.trim();
  if (type) {
    const normalized = type === 'all' ? 'allTypes' : type;
    patch.typesToDisplay = applyTypeFilterValue(DEFAULT_FILTER_URL_STATE.typesToDisplay, normalized);
  }

  const homeType = params.get('homeType')?.trim();
  if (homeType) {
    patch.mlsPropertyTypes = homeType.split(',').map((value) => value.trim()).filter(Boolean);
  }

  const school = params.get('school')?.trim();
  if (school) {
    patch.schoolDistricts = school.split(',').map((value) => value.trim()).filter(Boolean);
  }

  const stories = params.get('stories')?.trim();
  if (stories) {
    patch.stories = stories.split(',').map((value) => value.trim()).filter(Boolean);
  }

  const lotMin = params.get('lotMin');
  if (lotMin !== null) {
    patch.lotSizeMin = parseFilterNumber(lotMin, 0);
  }

  const lotMax = params.get('lotMax');
  if (lotMax !== null) {
    patch.lotSizeMax = parseFilterNumber(lotMax, DEFAULT_LOT_SIZE_MAX);
  }

  const sqftMin = params.get('sqftMin');
  if (sqftMin !== null) {
    patch.sqftMin = parseFilterNumber(sqftMin, 0);
  }

  const sqftMax = params.get('sqftMax');
  if (sqftMax !== null) {
    patch.sqftMax = parseFilterNumber(sqftMax, DEFAULT_SQFT_MAX);
  }

  const ppsfMin = params.get('ppsfMin');
  if (ppsfMin !== null) {
    patch.pricePerSqFtMin = parseFilterNumber(ppsfMin, 0);
  }

  const ppsfMax = params.get('ppsfMax');
  if (ppsfMax !== null) {
    patch.pricePerSqFtMax = parseFilterNumber(ppsfMax, DEFAULT_PRICE_PER_SQFT_MAX);
  }

  const domMax = params.get('domMax');
  if (domMax !== null) {
    patch.maxDaysOnMarket = parseFilterNumber(domMax, 0);
  }

  const yearMin = params.get('yearMin');
  if (yearMin !== null) {
    patch.yearBuiltMin = parseFilterNumber(yearMin, 0);
  }

  const yearMax = params.get('yearMax');
  if (yearMax !== null) {
    patch.yearBuiltMax = parseFilterNumber(yearMax, DEFAULT_YEAR_BUILT_MAX);
  }

  const tour = params.get('tour');
  if (tour === '1' || tour === 'true') {
    patch.requireVirtualTour = true;
  }

  const pool = params.get('pool');
  if (pool) {
    patch.poolFilter = parseYesNoFilter(pool);
  }

  const fireplace = params.get('fireplace');
  if (fireplace) {
    patch.fireplaceFilter = parseYesNoFilter(fireplace);
  }

  const garage = params.get('garage');
  if (garage === '1' || garage === 'true') {
    patch.requireGarage = true;
  }

  const minPrice = params.get('minPrice');
  if (minPrice !== null) {
    patch.minPrice = parseFilterNumber(minPrice, 0);
  }

  const maxPrice = params.get('maxPrice');
  if (maxPrice !== null) {
    patch.maxPrice = parseFilterNumber(maxPrice, 5_000_000);
  }

  const beds = params.get('beds');
  if (beds !== null) {
    patch.bedsMin = parseFilterNumber(beds, 0);
  }

  const baths = params.get('baths');
  if (baths !== null) {
    patch.minBaths = parseFilterNumber(baths, 0);
  }

  const areaMin = params.get('areaMin');
  if (areaMin !== null) {
    patch.areaMin = parseFilterNumber(areaMin, 0);
  }

  const areaMax = params.get('areaMax');
  if (areaMax !== null) {
    patch.areaMax = parseFilterNumber(areaMax, 1000);
  }

  const sizeMin = params.get('sizeMin');
  if (sizeMin !== null) {
    patch.sizeMin = parseFilterNumber(sizeMin, 0);
  }

  const sizeMax = params.get('sizeMax');
  if (sizeMax !== null) {
    patch.sizeMax = parseFilterNumber(sizeMax, 100_000);
  }

  const sort = params.get('sort');
  if (sort === 'asc' || sort === 'ascending') {
    patch.sort = 'ascending';
  } else if (sort === 'desc' || sort === 'descending') {
    patch.sort = 'descending';
  }

  return patch;
}

export function parseFilterUrlState(params: ParamMap): FilterUrlState {
  const state: FilterUrlState = {
    ...DEFAULT_FILTER_URL_STATE,
    typesToDisplay: { ...DEFAULT_FILTER_URL_STATE.typesToDisplay },
    mlsPropertyTypes: [],
    schoolDistricts: [],
    stories: [],
    listingStatuses: [],
  };

  const address = params.get('address')?.trim();
  if (address) {
    state.searchText = address;
  }

  state.searchBounds = parseGeoBounds(params.get('bounds'));

  const mapBounds = parseGeoBounds(params.get('mapBounds'));
  if (mapBounds) {
    state.mapBounds = mapBounds;
  }

  const mapFilter = params.get('mapFilter');
  state.filterByMapBounds = mapFilter === '1' || mapFilter === 'true';
  if (mapFilter === '0' || mapFilter === 'false') {
    state.filterByMapBounds = false;
  }

  const type = params.get('type')?.trim();
  if (type) {
    const normalized = type === 'all' ? 'allTypes' : type;
    state.typesToDisplay = applyTypeFilterValue(DEFAULT_FILTER_URL_STATE.typesToDisplay, normalized);
  }

  const homeType = params.get('homeType')?.trim();
  if (homeType) {
    state.mlsPropertyTypes = homeType.split(',').map((value) => value.trim()).filter(Boolean);
  }

  const school = params.get('school')?.trim();
  if (school) {
    state.schoolDistricts = school.split(',').map((value) => value.trim()).filter(Boolean);
  }

  const stories = params.get('stories')?.trim();
  if (stories) {
    state.stories = stories.split(',').map((value) => value.trim()).filter(Boolean);
  }

  const status = params.get('status')?.trim();
  if (status) {
    state.listingStatuses = status
      .split(',')
      .map((value) => normalizeListingStatusFilterValue(value.trim()))
      .filter((value): value is ListingStatusFilterKey => value !== null);
  }

  const lotMin = params.get('lotMin');
  if (lotMin !== null) {
    state.lotSizeMin = parseFilterNumber(lotMin, 0);
  }

  const lotMax = params.get('lotMax');
  if (lotMax !== null) {
    state.lotSizeMax = parseFilterNumber(lotMax, DEFAULT_LOT_SIZE_MAX);
  }

  const sqftMin = params.get('sqftMin');
  if (sqftMin !== null) {
    state.sqftMin = parseFilterNumber(sqftMin, 0);
  }

  const sqftMax = params.get('sqftMax');
  if (sqftMax !== null) {
    state.sqftMax = parseFilterNumber(sqftMax, DEFAULT_SQFT_MAX);
  }

  const ppsfMin = params.get('ppsfMin');
  if (ppsfMin !== null) {
    state.pricePerSqFtMin = parseFilterNumber(ppsfMin, 0);
  }

  const ppsfMax = params.get('ppsfMax');
  if (ppsfMax !== null) {
    state.pricePerSqFtMax = parseFilterNumber(ppsfMax, DEFAULT_PRICE_PER_SQFT_MAX);
  }

  const domMax = params.get('domMax');
  if (domMax !== null) {
    state.maxDaysOnMarket = parseFilterNumber(domMax, 0);
  }

  const yearMin = params.get('yearMin');
  if (yearMin !== null) {
    state.yearBuiltMin = parseFilterNumber(yearMin, 0);
  }

  const yearMax = params.get('yearMax');
  if (yearMax !== null) {
    state.yearBuiltMax = parseFilterNumber(yearMax, DEFAULT_YEAR_BUILT_MAX);
  }

  const tour = params.get('tour');
  state.requireVirtualTour = tour === '1' || tour === 'true';

  const pool = params.get('pool');
  if (pool) {
    state.poolFilter = parseYesNoFilter(pool);
  }

  const fireplace = params.get('fireplace');
  if (fireplace) {
    state.fireplaceFilter = parseYesNoFilter(fireplace);
  }

  const garage = params.get('garage');
  state.requireGarage = garage === '1' || garage === 'true';

  const minPrice = params.get('minPrice');
  if (minPrice !== null) {
    state.minPrice = parseFilterNumber(minPrice, 0);
  }

  const maxPrice = params.get('maxPrice');
  if (maxPrice !== null) {
    state.maxPrice = parseFilterNumber(maxPrice, 5_000_000);
  }

  const beds = params.get('beds');
  if (beds !== null) {
    state.bedsMin = parseFilterNumber(beds, 0);
  }

  const baths = params.get('baths');
  if (baths !== null) {
    state.minBaths = parseFilterNumber(baths, 0);
  }

  const areaMin = params.get('areaMin');
  if (areaMin !== null) {
    state.areaMin = parseFilterNumber(areaMin, 0);
  }

  const areaMax = params.get('areaMax');
  if (areaMax !== null) {
    state.areaMax = parseFilterNumber(areaMax, 1000);
  }

  const sizeMin = params.get('sizeMin');
  if (sizeMin !== null) {
    state.sizeMin = parseFilterNumber(sizeMin, 0);
  }

  const sizeMax = params.get('sizeMax');
  if (sizeMax !== null) {
    state.sizeMax = parseFilterNumber(sizeMax, 100_000);
  }

  const sort = params.get('sort');
  if (sort === 'asc' || sort === 'ascending') {
    state.sort = 'ascending';
  } else if (sort === 'desc' || sort === 'descending') {
    state.sort = 'descending';
  }

  return state;
}

export function serializeFilterQueryParams(state: FilterUrlState): Record<string, string | null> {
  const params: Record<string, string | null> = {};

  params.address = state.searchText.trim() || null;
  params.bounds = serializeGeoBounds(state.searchBounds);
  params.mapBounds = state.filterByMapBounds ? serializeGeoBounds(state.mapBounds) : null;
  params.mapFilter = state.filterByMapBounds ? '1' : null;

  const category = getCurrentTypeFilter(state.typesToDisplay);
  params.type = category === 'all' ? null : category;

  params.homeType = state.mlsPropertyTypes.length > 0 ? state.mlsPropertyTypes.join(',') : null;
  params.school = state.schoolDistricts.length > 0 ? state.schoolDistricts.join(',') : null;
  params.stories = state.stories.length > 0 ? state.stories.join(',') : null;
  params.status = state.listingStatuses.length > 0 ? state.listingStatuses.join(',') : null;
  params.lotMin = state.lotSizeMin > 0 ? String(state.lotSizeMin) : null;
  params.lotMax = state.lotSizeMax < DEFAULT_LOT_SIZE_MAX ? String(state.lotSizeMax) : null;
  params.sqftMin = state.sqftMin > 0 ? String(state.sqftMin) : null;
  params.sqftMax = state.sqftMax < DEFAULT_SQFT_MAX ? String(state.sqftMax) : null;
  params.ppsfMin = state.pricePerSqFtMin > 0 ? String(state.pricePerSqFtMin) : null;
  params.ppsfMax =
    state.pricePerSqFtMax < DEFAULT_PRICE_PER_SQFT_MAX ? String(state.pricePerSqFtMax) : null;
  params.domMax = state.maxDaysOnMarket > 0 ? String(state.maxDaysOnMarket) : null;
  params.yearMin = state.yearBuiltMin > 0 ? String(state.yearBuiltMin) : null;
  params.yearMax = state.yearBuiltMax < DEFAULT_YEAR_BUILT_MAX ? String(state.yearBuiltMax) : null;
  params.tour = state.requireVirtualTour ? '1' : null;
  params.pool = state.poolFilter !== 'any' ? state.poolFilter : null;
  params.fireplace = state.fireplaceFilter !== 'any' ? state.fireplaceFilter : null;
  params.garage = state.requireGarage ? '1' : null;
  params.minPrice = state.minPrice > 0 ? String(state.minPrice) : null;
  params.maxPrice = state.maxPrice < 5_000_000 ? String(state.maxPrice) : null;
  params.beds = state.bedsMin > 0 ? String(state.bedsMin) : null;
  params.baths = state.minBaths > 0 ? String(state.minBaths) : null;
  params.areaMin = state.areaMin > 0 ? String(state.areaMin) : null;
  params.areaMax = state.areaMax < 1000 ? String(state.areaMax) : null;
  params.sizeMin = state.sizeMin > 0 ? String(state.sizeMin) : null;
  params.sizeMax = state.sizeMax < 100_000 ? String(state.sizeMax) : null;
  params.sort = state.sort === 'ascending' ? 'asc' : null;

  return params;
}

export function filterQueryParamsEqual(
  left: Record<string, string | null | undefined>,
  right: Record<string, string | null | undefined>,
): boolean {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    const leftValue = left[key] ?? null;
    const rightValue = right[key] ?? null;
    if (leftValue !== rightValue) {
      return false;
    }
  }
  return true;
}

export function filterUrlStatesEqual(left: FilterUrlState, right: FilterUrlState): boolean {
  return filterQueryParamsEqual(
    serializeFilterQueryParams(left),
    serializeFilterQueryParams(right),
  );
}

export function bedFilterIdFromMin(bedsMin: number): string {
  if (bedsMin >= 5) return 'bed-filter-five';
  if (bedsMin === 4) return 'bed-filter-four';
  if (bedsMin === 3) return 'bed-filter-three';
  if (bedsMin === 2) return 'bed-filter-two';
  if (bedsMin === 1) return 'bed-filter-one';
  return 'bed-filter-any';
}

export function bathFilterIdFromMin(minBaths: number): string {
  if (minBaths >= 4) return 'bath-filter-four';
  if (minBaths === 3) return 'bath-filter-three';
  if (minBaths === 2) return 'bath-filter-two';
  if (minBaths === 1) return 'bath-filter-one';
  return 'bath-filter-any';
}

export function typeFilterValueFromCategory(category: ListingTypeFilter): string {
  return category === 'all' ? 'allTypes' : category;
}
